import { useState } from 'react';
import {
  useBankAccounts,
  useCreateBankAccount,
  useUpdateBankAccount,
  useDeleteBankAccount,
} from '../../../hooks/useBankAccounts';
import {
  ITEMS_PER_PAGE,
  INITIAL_BANK_ACCOUNT_STATE,
} from '../constants/bankAccountsConstants';
import { filterBankAccounts } from '../utils/bankAccountsUtils';
import { usePaginatedFilter } from '../../../common/hooks/usePaginatedFilter';

export const useBankAccountsTable = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedBankAccount, setSelectedBankAccount] = useState(INITIAL_BANK_ACCOUNT_STATE);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);

  const { data: bankAccounts = [], isLoading } = useBankAccounts();
  const createBankAccountMutation = useCreateBankAccount();
  const updateBankAccountMutation = useUpdateBankAccount();
  const deleteBankAccountMutation = useDeleteBankAccount();

  const {
    data: paginatedAccounts,
    totalCount,
    totalPages,
    page,
    rowsPerPage,
    searchTerm,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
  } = usePaginatedFilter({
    data: bankAccounts,
    itemsPerPage: ITEMS_PER_PAGE,
    filterFn: filterBankAccounts,
  });

  const handleOpenDialog = () => {
    setSelectedBankAccount(INITIAL_BANK_ACCOUNT_STATE);
    setEditMode(false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedBankAccount(INITIAL_BANK_ACCOUNT_STATE);
    setEditMode(false);
  };

  const handleEdit = (bankAccount) => {
    setSelectedBankAccount(bankAccount);
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleCreate = async (payload) => {
    await createBankAccountMutation.mutateAsync(payload);
  };

  const handleUpdate = async (uuid, payload) => {
    await updateBankAccountMutation.mutateAsync({ id: uuid, data: payload });
  };

  const handleDeleteRequest = (bankAccount) => {
    setAccountToDelete(bankAccount);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!accountToDelete) return;
    await deleteBankAccountMutation.mutateAsync(accountToDelete.uuid);
    setDeleteDialogOpen(false);
    setAccountToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setAccountToDelete(null);
  };

  const mutationsLoading =
    createBankAccountMutation.isPending ||
    updateBankAccountMutation.isPending ||
    deleteBankAccountMutation.isPending;

  return {
    data: paginatedAccounts,
    isLoading,
    totalPages,
    totalCount,
    page,
    rowsPerPage,
    searchTerm,
    dialogOpen,
    editMode,
    selectedBankAccount,
    deleteDialogOpen,
    accountToDelete,
    mutationsLoading,

    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleOpenDialog,
    handleCloseDialog,
    handleEdit,
    handleCreate,
    handleUpdate,
    handleDeleteRequest,
    handleConfirmDelete,
    handleCancelDelete,
  };
};
